import * as React from 'react';

import moment from 'moment';

import {colors} from '../../colors/colors';
import {DateTimePickerPopover} from '../../date-time-picker/date-time-picker-popover';
import {ClockRegularIcon} from '../../icon/svg/clock-regular-icon';
import {SkipIcon} from '../../icon/svg/skip-icon';
// @ts-expect-error - imported from .js/.jsx file
import {TickCircleIcon} from '../../icon/svg/tick-circle-icon';
import {Link} from '../../link/link';
import {FormattedTimestamp} from '../../timestamp/formatted-timestamp';
import {Body} from '../../typography/body';

type Props = {
    dueTime: number | null | undefined;
    isOverdue: boolean;
    completedBy: string | null | undefined;
    completedTime: number | null | undefined;
    isSkipped: boolean | null | undefined;
    onDueTimeChange: (dueTime: number) => void;
    isAutomated: boolean;
};

export function TaskStatusLabel(props: Props) {
    const {onDueTimeChange} = props;

    const [shouldShowDatePicker, setShouldShowDatePicker] = React.useState<boolean>(false);
    const datePickerRef = React.useRef<HTMLElement | null | undefined>(null);

    const handleDueTimeChange = React.useCallback(
        (dueTime: Date) => {
            onDueTimeChange(moment(dueTime).unix());
            setShouldShowDatePicker(false);
        },
        [onDueTimeChange]
    );

    const handleCancel = React.useCallback(() => {
        setShouldShowDatePicker(false);
    }, []);

    const handleClick = React.useCallback(() => {
        setShouldShowDatePicker(true);
    }, []);

    const isDueTimeCurrentYear = React.useMemo(
        () => (props.dueTime ? moment.unix(props.dueTime).isSame(moment(), 'year') : false),
        [props.dueTime]
    );

    const dueTimeFormat = isDueTimeCurrentYear
        ? FormattedTimestamp.formats.shortMonthDayWithTimeCapitalized
        : FormattedTimestamp.formats.shortMonthDayWithYearWithTimeCapitalized;

    if (props.completedTime) {
        return (
            <div className='flex align-center gap-4'>
                <TickCircleIcon size={13} color={colors.lead} wrapWithDiv={false} />
                <Body size='small' color='light'>
                    Completed on{' '}
                </Body>
                <Body size='small' color='light'>
                    <FormattedTimestamp
                        format={FormattedTimestamp.formats.monthDayYear}
                        datetime={props.completedTime}
                        showDetailedPopover={true}
                    />
                </Body>
                <Body size='small' color='light'>
                    by {props.completedBy}
                </Body>
            </div>
        );
    }

    if (props.isSkipped) {
        return (
            <div className='flex align-center gap-4'>
                {/* @ts-expect-error */}
                <SkipIcon size={16} color={colors.grey} wrapWithDiv={false} />
                <Body size='small' color='medium'>
                    Skipped
                </Body>
                {props.completedBy && (
                    <Body size='small' color='medium'>
                        by {props.completedBy}
                    </Body>
                )}
            </div>
        );
    }

    if (!props.dueTime) {
        return null;
    }

    return (
        <>
            <div className='flex align-center gap-4 text-nowrap mr-8'>
                <ClockRegularIcon
                    size={16}
                    // @ts-expect-error
                    color={props.isOverdue ? colors.rose : colors.grey}
                    wrapWithDiv={false}
                />
                <Body size='small' color={props.isOverdue ? 'error' : 'medium'}>
                    Due
                </Body>
                {/* @ts-expect-error */}
                <span ref={datePickerRef}>
                    <DatePickerWrapper
                        isAutomated={props.isAutomated}
                        isOverdue={props.isOverdue}
                        handleClick={handleClick}
                        dueTime={props.dueTime}
                    >
                        <FormattedTimestamp format={dueTimeFormat} datetime={props.dueTime} />
                    </DatePickerWrapper>
                </span>
            </div>
            {shouldShowDatePicker && datePickerRef.current && (
                <DateTimePickerPopover
                    anchor={datePickerRef.current}
                    location='bottom'
                    shouldOmitTime={false}
                    minDate={moment().startOf('day').toDate()}
                    value={moment.unix(props.dueTime).toDate()}
                    onConfirm={handleDueTimeChange}
                    onCancel={handleCancel}
                    saveDateOnYearChange={true}
                    preventPropagation={true}
                />
            )}
        </>
    );
}

type DatePickerWrapperProps = {
    children: React.ReactNode;
    isAutomated: boolean;
    isOverdue: boolean;
    dueTime: number | null | undefined;
    handleClick: () => void;
};

function DatePickerWrapper(props: DatePickerWrapperProps) {
    if (props.isAutomated || !props.dueTime) {
        return (
            <Body size='small' color={props.isOverdue ? 'error' : 'medium'}>
                {props.children}
            </Body>
        );
    }

    return (
        <Link
            variant={props.isOverdue ? 'danger' : 'secondary'}
            onClick={(e) => {
                e.stopPropagation();
                props.handleClick();
            }}
            size='small'
        >
            {props.children}
        </Link>
    );
}
